import { tgpu, d, std } from 'typegpu';
import type { TgpuRoot, TgpuBuffer, StorageFlag } from 'typegpu';
import type { WgslArray } from 'typegpu/data';
import type { SimulationConfig } from '../../config/schema';
import { computeCorners, rigidPack } from '../../config/corners';
import { RigidState, RigidParams, DivParams, DataCell } from './types';
import { computeAccelerations, computeBob2 } from './deriv';
import { hash, samplePerturb } from '../shared/hash';
import { vizModeToNumber } from '../shared/constants';
import { SingleStateSystem } from '../shared/SingleStateSystem';
import type { SingleGpuPipelinesResult, SingleDivGpuPipelinesResult, NeighborPipelinesResult } from '../shared/SingleStateSystem';
import { buildRigidNeighborPipelines } from './RigidNeighbor';

export class RigidSystem extends SingleStateSystem<typeof RigidState, typeof RigidParams, typeof DivParams, typeof DataCell> {
  readonly key = 'rigid' as const;
  readonly stateSize = 16;

  stateArrayType(cellCount: number): WgslArray<typeof RigidState> { return d.arrayOf(RigidState, cellCount); }
  dataArrayType(cellCount: number): WgslArray<typeof DataCell> { return d.arrayOf(DataCell, cellCount); }
  paramsStruct(): typeof RigidParams { return RigidParams; }
  divParamsStruct(): typeof DivParams { return DivParams; }
  zeroState(): Record<string, number>[] {
    return Array.from({ length: this.cellCount }, () => ({ theta1: 0, omega1: 0, theta2: 0, omega2: 0 }));
  }

  protected buildGpuPipelines(root: TgpuRoot, stateA: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag, stateB: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag): SingleGpuPipelinesResult {
    const initLayout = tgpu.bindGroupLayout({
      state: { storage: this.StateArray, access: 'mutable' },
      params: { uniform: RigidParams },
    });
    const stepLayout = tgpu.bindGroupLayout({
      currentState: { storage: this.StateArray },
      nextState: { storage: this.StateArray, access: 'mutable' },
      params: { uniform: RigidParams },
    });
    const accumulateLayout = tgpu.bindGroupLayout({
      currentState: { storage: this.StateArray },
      data: { storage: this.DataArray, access: 'mutable' },
      params: { uniform: RigidParams },
    });

    const initCell = (ci: number) => {
      'use gpu';
      const p = initLayout.$.params;
      const x = ci % d.u32(p.resolution); const y = ci / d.u32(p.resolution);
      const u = d.f32(x) / p.resolution; const v = d.f32(y) / p.resolution;
      const t1 = (1-u)*(1-v)*p.c00_t1+u*(1-v)*p.c10_t1+(1-u)*v*p.c01_t1+u*v*p.c11_t1;
      const w1 = (1-u)*(1-v)*p.c00_w1+u*(1-v)*p.c10_w1+(1-u)*v*p.c01_w1+u*v*p.c11_w1;
      const t2 = (1-u)*(1-v)*p.c00_t2+u*(1-v)*p.c10_t2+(1-u)*v*p.c01_t2+u*v*p.c11_t2;
      const w2 = (1-u)*(1-v)*p.c00_w2+u*(1-v)*p.c10_w2+(1-u)*v*p.c01_w2+u*v*p.c11_w2;
      initLayout.$.state[ci] = RigidState({ theta1: t1, omega1: w1, theta2: t2, omega2: w2 });
    };

    const stepCell = (ci: number) => {
      'use gpu';
      const s = stepLayout.$.currentState[ci]; const p = stepLayout.$.params; const dt = p.dt;
      const a0 = computeAccelerations(s.theta1, s.omega1, s.theta2, s.omega2, p.m1, p.m2, p.L1, p.L2);
      const hw1 = s.omega1 + 0.5*dt*a0.x; const hw2 = s.omega2 + 0.5*dt*a0.y;
      const nt1 = s.theta1 + dt*hw1; const nt2 = s.theta2 + dt*hw2;
      const a1 = computeAccelerations(nt1, hw1, nt2, hw2, p.m1, p.m2, p.L1, p.L2);
      stepLayout.$.nextState[ci] = RigidState({ theta1: nt1, omega1: hw1+0.5*dt*a1.x, theta2: nt2, omega2: hw2+0.5*dt*a1.y });
    };

    const accumulateCell = (ci: number) => {
      'use gpu';
      const s = accumulateLayout.$.currentState[ci]; const data = accumulateLayout.$.data[ci]; const p = accumulateLayout.$.params;
      const vm = p.vizMode;
      if (vm < 0.5) {
        const bob = computeBob2(s.theta1, s.theta2, p.L1, p.L2);
        if (data.a > 0.5) { const dx = bob.x-data.r; const dy = bob.y-data.g; const dist = std.sqrt(dx*dx+dy*dy); accumulateLayout.$.data[ci] = DataCell({ r: bob.x, g: bob.y, b: data.b+dist, a: 1 }); }
        else { accumulateLayout.$.data[ci] = DataCell({ r: bob.x, g: bob.y, b: 0, a: 1 }); }
      } else if (vm < 1.5) {
        const bob = computeBob2(s.theta1, s.theta2, p.L1, p.L2);
        accumulateLayout.$.data[ci] = DataCell({ r: bob.x, g: bob.y, b: 0, a: 1 });
      } else if (vm < 3.5) {
        const bob = computeBob2(s.theta1, s.theta2, p.L1, p.L2);
        if (data.a > 0.5) { const dx = bob.x-data.r; const dy = bob.y-data.g; const dist = std.sqrt(dx*dx+dy*dy); accumulateLayout.$.data[ci] = DataCell({ r: bob.x, g: bob.y, b: data.b+dist, a: 1 }); }
        else { accumulateLayout.$.data[ci] = DataCell({ r: bob.x, g: bob.y, b: 0, a: 1 }); }
      } else {
        const res = d.u32(p.resolution);
        const cx = ci % res; const cy = ci / res;
        const bob = computeBob2(s.theta1, s.theta2, p.L1, p.L2);
        const nx0 = std.min(cx + 1, res - 1);
        const nx1 = std.max(d.i32(cx) - 1, 0);
        const ny0 = std.min(cy + 1, res - 1);
        const ny1 = std.max(d.i32(cy) - 1, 0);
        const s0 = accumulateLayout.$.currentState[d.u32(ny0)*res+cx];
        const s1 = accumulateLayout.$.currentState[d.u32(ny1)*res+cx];
        const s2 = accumulateLayout.$.currentState[cy*res+nx0];
        const s3 = accumulateLayout.$.currentState[cy*res+d.u32(nx1)];
        const b0 = computeBob2(s0.theta1, s0.theta2, p.L1, p.L2);
        const b1 = computeBob2(s1.theta1, s1.theta2, p.L1, p.L2);
        const b2 = computeBob2(s2.theta1, s2.theta2, p.L1, p.L2);
        const b3 = computeBob2(s3.theta1, s3.theta2, p.L1, p.L2);
        const avgDx = (bob.x-b0.x + bob.x-b1.x + bob.x-b2.x + bob.x-b3.x) * 0.25;
        const avgDy = (bob.y-b0.y + bob.y-b1.y + bob.y-b2.y + bob.y-b3.y) * 0.25;
        const avgDist = std.sqrt(avgDx*avgDx + avgDy*avgDy);
        if (vm < 4.5) {
          accumulateLayout.$.data[ci] = DataCell({ r: bob.x, g: bob.y, b: avgDist, a: 1 });
        } else {
          if (data.a > 0.5) {
            accumulateLayout.$.data[ci] = DataCell({ r: bob.x, g: bob.y, b: data.b + avgDist, a: 1 });
          } else {
            accumulateLayout.$.data[ci] = DataCell({ r: bob.x, g: bob.y, b: avgDist, a: 1 });
          }
        }
      }
    };

    return {
      initLayout, stepLayout, accumulateLayout,
      initPipeline: root.createGuardedComputePipeline(initCell),
      stepPipeline: root.createGuardedComputePipeline(stepCell),
      accumulatePipeline: root.createGuardedComputePipeline(accumulateCell),
      initBG: root.createBindGroup(initLayout, { state: stateA, params: this.paramsBuffer }),
      stepFwd: root.createBindGroup(stepLayout, { currentState: stateA, nextState: stateB, params: this.paramsBuffer }),
      stepBwd: root.createBindGroup(stepLayout, { currentState: stateB, nextState: stateA, params: this.paramsBuffer }),
      accA: root.createBindGroup(accumulateLayout, { currentState: stateA, data: this.dataBuf, params: this.paramsBuffer }),
      accB: root.createBindGroup(accumulateLayout, { currentState: stateB, data: this.dataBuf, params: this.paramsBuffer }),
    };
  }

  protected buildDivGpuPipelines(root: TgpuRoot, stateA: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag, stateB: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag): SingleDivGpuPipelinesResult {
    const divInitLayout = tgpu.bindGroupLayout({
      baseState: { storage: this.StateArray, access: 'mutable' },
      pertState: { storage: this.StateArray, access: 'mutable' },
      divData: { storage: this.DataArray, access: 'mutable' },
      params: { uniform: DivParams },
    });
    const divStepLayout = tgpu.bindGroupLayout({
      baseCurrent: { storage: this.StateArray }, pertCurrent: { storage: this.StateArray },
      baseNext: { storage: this.StateArray, access: 'mutable' }, pertNext: { storage: this.StateArray, access: 'mutable' },
      divData: { storage: this.DataArray, access: 'mutable' }, params: { uniform: DivParams },
    });
    const TWO_PI = 2 * std.acos(0);
    const circDiff = (a: number) => { 'use gpu'; return a - std.floor(a / TWO_PI + 0.5) * TWO_PI; };

    const divInitCell = (ci: number) => {
      'use gpu';
      const p = divInitLayout.$.params;
      const x = ci % d.u32(p.resolution); const y = ci / d.u32(p.resolution);
      const u = d.f32(x) / p.resolution; const v = d.f32(y) / p.resolution;
      const t1 = (1-u)*(1-v)*p.c00_t1+u*(1-v)*p.c10_t1+(1-u)*v*p.c01_t1+u*v*p.c11_t1;
      const w1 = (1-u)*(1-v)*p.c00_w1+u*(1-v)*p.c10_w1+(1-u)*v*p.c01_w1+u*v*p.c11_w1;
      const t2 = (1-u)*(1-v)*p.c00_t2+u*(1-v)*p.c10_t2+(1-u)*v*p.c01_t2+u*v*p.c11_t2;
      const w2 = (1-u)*(1-v)*p.c00_w2+u*(1-v)*p.c10_w2+(1-u)*v*p.c01_w2+u*v*p.c11_w2;
      divInitLayout.$.baseState[ci] = RigidState({ theta1: t1, omega1: w1, theta2: t2, omega2: w2 });
      const px = d.f32(x) / p.resolution; const py = d.f32(y) / p.resolution;
      const h1 = samplePerturb(px*1000+p.seed, py*1000+p.seed, p.perturb, p.perturbMode);
      const h2 = samplePerturb(px*1000+100+p.seed, py*1000+p.seed, p.perturb, p.perturbMode);
      divInitLayout.$.pertState[ci] = RigidState({ theta1: t1+h1, omega1: w1, theta2: t2+h2, omega2: w2 });
      divInitLayout.$.divData[ci] = DataCell({ r: 0, g: 0, b: 0, a: 1 });
    };

    const divStepCell = (ci: number) => {
      'use gpu';
      const p = divStepLayout.$.params;
      const dt = p.dt;
      const base = divStepLayout.$.baseCurrent[ci];
      const pert = divStepLayout.$.pertCurrent[ci];
      const ba0 = computeAccelerations(base.theta1, base.omega1, base.theta2, base.omega2, p.m1, p.m2, p.L1, p.L2);
      let bw1 = base.omega1 + 0.5 * dt * ba0.x;
      let bw2 = base.omega2 + 0.5 * dt * ba0.y;
      let bt1 = base.theta1 + dt * bw1;
      let bt2 = base.theta2 + dt * bw2;
      const ba1 = computeAccelerations(bt1, bw1, bt2, bw2, p.m1, p.m2, p.L1, p.L2);
      bw1 += 0.5 * dt * ba1.x;
      bw2 += 0.5 * dt * ba1.y;
      divStepLayout.$.baseNext[ci] = RigidState({ theta1: bt1, omega1: bw1, theta2: bt2, omega2: bw2 });
      const pa0 = computeAccelerations(pert.theta1, pert.omega1, pert.theta2, pert.omega2, p.m1, p.m2, p.L1, p.L2);
      let pw1 = pert.omega1 + 0.5 * dt * pa0.x;
      let pw2 = pert.omega2 + 0.5 * dt * pa0.y;
      let pt1 = pert.theta1 + dt * pw1;
      let pt2 = pert.theta2 + dt * pw2;
      const pa1 = computeAccelerations(pt1, pw1, pt2, pw2, p.m1, p.m2, p.L1, p.L2);
      pw1 += 0.5 * dt * pa1.x;
      pw2 += 0.5 * dt * pa1.y;
      divStepLayout.$.pertNext[ci] = RigidState({ theta1: pt1, omega1: pw1, theta2: pt2, omega2: pw2 });
      const da1 = circDiff(bt1 - pt1);
      const dw1 = bw1 - pw1;
      const da2 = circDiff(bt2 - pt2);
      const dw2 = bw2 - pw2;
      const dist = std.sqrt(da1*da1+dw1*dw1+da2*da2+dw2*dw2);
      const data = divStepLayout.$.divData[ci];
      if (data.g < 0.5) {
        if (dist > 0.05) { divStepLayout.$.divData[ci] = DataCell({ r: p.frameCounter, g: 1, b: dist, a: 1 }); }
      } else {
        divStepLayout.$.divData[ci] = DataCell({ r: data.r, g: 1, b: dist, a: 1 });
      }
    };

    return {
      divInitLayout, divStepLayout,
      divInitPipeline: root.createGuardedComputePipeline(divInitCell),
      divStepPipeline: root.createGuardedComputePipeline(divStepCell),
      divInitBG: root.createBindGroup(divInitLayout, { baseState: stateA, pertState: this.pertStateA, divData: this.divDataBuf, params: this.divParamsBuffer }),
      divStepFwd: root.createBindGroup(divStepLayout, { baseCurrent: stateA, pertCurrent: this.pertStateA, baseNext: stateB, pertNext: this.pertStateB, divData: this.divDataBuf, params: this.divParamsBuffer }),
      divStepBwd: root.createBindGroup(divStepLayout, { baseCurrent: stateB, pertCurrent: this.pertStateB, baseNext: stateA, pertNext: this.pertStateA, divData: this.divDataBuf, params: this.divParamsBuffer }),
    };
  }

  buildParamsData(config: SimulationConfig): Record<string, number> {
    this.lastConfig = config;
    return packRigidParams(config);
  }

  buildDivParamsData(config: SimulationConfig, seed: number, perturb: number): Record<string, number> & { frameCounter: number } {
    return { ...packRigidParams(config), seed, perturb, perturbMode: config.perturbDistribution === 'gaussian' ? 1 : 0, frameCounter: 0 };
  }

  protected buildClosenessOfReturnPipeline(
    root: TgpuRoot,
    stateA: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag,
    stateB: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag,
    snapshotBuf: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag,
    dataBuf: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag,
  ): import('../shared/SingleStateSystem').SingleCORPipelineResult {
    const TWO_PI = 6.28318530718;
    const circDiff = (a: number) => { 'use gpu'; return a - std.floor(a / TWO_PI + 0.5) * TWO_PI; };

    const ttrLayout = tgpu.bindGroupLayout({
      currentState: { storage: this.StateArray },
      snapshot: { storage: this.StateArray },
      nextState: { storage: this.StateArray, access: 'mutable' },
      data: { storage: this.DataArray, access: 'mutable' },
      params: { uniform: RigidParams },
      ttr: { uniform: d.struct({ currentIter: d.f32, minImprovement: d.f32, _p0: d.f32, _p1: d.f32 }) },
    });
    const tl = ttrLayout;

    const ttrCell = (ci: number) => {
      'use gpu';
      const s = tl.$.currentState[ci]; const snap = tl.$.snapshot[ci]; const p = tl.$.params; const dt = p.dt;
      const a0 = computeAccelerations(s.theta1, s.omega1, s.theta2, s.omega2, p.m1, p.m2, p.L1, p.L2);
      const hw1 = s.omega1 + 0.5 * dt * a0.x; const hw2 = s.omega2 + 0.5 * dt * a0.y;
      const nt1 = s.theta1 + dt * hw1; const nt2 = s.theta2 + dt * hw2;
      const a1 = computeAccelerations(nt1, hw1, nt2, hw2, p.m1, p.m2, p.L1, p.L2);
      tl.$.nextState[ci] = RigidState({ theta1: nt1, omega1: hw1 + 0.5 * dt * a1.x, theta2: nt2, omega2: hw2 + 0.5 * dt * a1.y });
      const dt1 = circDiff(nt1 - snap.theta1); const dw1 = (hw1 + 0.5 * dt * a1.x) - snap.omega1;
      const dt2 = circDiff(nt2 - snap.theta2); const dw2 = (hw2 + 0.5 * dt * a1.y) - snap.omega2;
      const dist = std.sqrt(dt1 * dt1 + dw1 * dw1 + dt2 * dt2 + dw2 * dw2);
      const data = tl.$.data[ci]; const ttr = tl.$.ttr;
      if (data.a < 0.5) {
        tl.$.data[ci] = DataCell({ r: ttr.currentIter, g: 0, b: dist, a: 1 });
      } else if (dist < data.b - ttr.minImprovement) {
        tl.$.data[ci] = DataCell({ r: ttr.currentIter, g: data.g, b: dist, a: data.a });
      }
    };

    const pipeline = root.createGuardedComputePipeline(ttrCell);
    return {
      ttrPipeline: pipeline,
      ttrFwd: root.createBindGroup(ttrLayout, { currentState: stateA as never, snapshot: snapshotBuf as never, nextState: stateB as never, data: dataBuf as never, params: this.paramsBuffer as never, ttr: this.ttrUniformsBuf as never }),
      ttrBwd: root.createBindGroup(ttrLayout, { currentState: stateB as never, snapshot: snapshotBuf as never, nextState: stateA as never, data: dataBuf as never, params: this.paramsBuffer as never, ttr: this.ttrUniformsBuf as never }),
    };
  }

  protected buildNeighborPipelines(
    root: TgpuRoot,
    stateA: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag,
    stateB: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag,
  ): NeighborPipelinesResult {
    return buildRigidNeighborPipelines({
      root,
      StateArray: this.StateArray,
      DataArray: this.DataArray,
      NbStackArray: this.nbStackArray,
      paramsBuffer: this.paramsBuffer,
      nbParamsBuffer: this.nbParamsBuffer,
      baseA: stateA,
      baseB: stateB,
      nbPertA: this.nbPertA,
      nbPertB: this.nbPertB,
      nbDataBuf: this.nbDataBuf,
    });
  }
}

function packRigidParams(config: SimulationConfig): Record<string, number> {
  const corners = computeCorners(config);
  const c = (i: number) => rigidPack(corners[i]);
  return {
    m1: config.m1, m2: config.m2, L1: config.L1, L2: config.L2,
    dt: config.dt, resolution: config.resolution, vizMode: vizModeToNumber(config.vizMode),
    c00_t1: c(0)[0], c00_w1: c(0)[1], c00_t2: c(0)[2], c00_w2: c(0)[3],
    c10_t1: c(1)[0], c10_w1: c(1)[1], c10_t2: c(1)[2], c10_w2: c(1)[3],
    c01_t1: c(2)[0], c01_w1: c(2)[1], c01_t2: c(2)[2], c01_w2: c(2)[3],
    c11_t1: c(3)[0], c11_w1: c(3)[1], c11_t2: c(3)[2], c11_w2: c(3)[3],
  };
}